import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../lib/auth";

const API = process.env.REACT_APP_BACKEND_URL || "";

/**
 * Authenticated fetch that reuses the existing Femcare admin session.
 *   • sends the HttpOnly `access_token` cookie via `credentials:"include"`
 *   • also passes `Authorization: Bearer <accessToken>` (belt + suspenders) when
 *     the auth context exposes a token — useful when the cookie SameSite blocks it.
 */
async function adminFetch(path, opts = {}, token) {
    const headers = { ...(opts.headers || {}) };
    if (token) headers["Authorization"] = `Bearer ${token}`;
    const res = await fetch(API + path, { credentials: "include", ...opts, headers });
    if (!res.ok) {
        const t = await res.text().catch(() => "");
        const err = new Error(t || `HTTP ${res.status}`); err.status = res.status; throw err;
    }
    return res;
}

function fmt(minor, ccy) {
    if (minor == null) return "";
    const major = Number(minor) / 100;
    if (ccy === "USD") return `$${major.toFixed(2)}`;
    return `₹${major.toFixed(major % 1 === 0 ? 0 : 2)}`;
}

export default function AdminConversions() {
    const { user } = useAuth();
    const accessToken = typeof window !== "undefined" ? localStorage.getItem("femcare_token") : null;
    const navigate = useNavigate();
    const location = useLocation();

    // Route guard — unauthenticated ⇒ send to /admin/login (existing flow).
    useEffect(() => {
        if (user === false) navigate("/admin/login", { replace: true });
        else if (user && user.role && user.role !== "admin") navigate("/", { replace: true });
    }, [user, navigate]);

    const [kpi, setKpi] = useState(null);
    const [rows, setRows] = useState([]);
    const [total, setTotal] = useState(0);
    const [publishers, setPublishers] = useState([]);
    const [campaigns, setCampaigns] = useState([]);
    // Pre-fill filters from URL params so "View Conversions" links from
    // Publisher Links deep-link into a pre-filtered view.
    const [filters, setFilters] = useState(() => {
        const p = new URLSearchParams(location.search);
        return {
            status: p.get("status") || "CONFIRMED",
            per_page: parseInt(p.get("per_page") || "50", 10) || 50,
            page: parseInt(p.get("page") || "1", 10) || 1,
            q: p.get("q") || "",
            publisher_id: p.get("publisher_id") || "",
            utm_source: p.get("utm_source") || "",
            utm_campaign: p.get("utm_campaign") || "",
        };
    });
    const [tab, setTab] = useState("table");
    const [err, setErr] = useState(null);
    const [loading, setLoading] = useState(false);

    const qs = useMemo(() => {
        const p = new URLSearchParams();
        Object.entries(filters).forEach(([k, v]) => { if (v !== "" && v != null) p.set(k, v); });
        return p.toString();
    }, [filters]);

    const load = async () => {
        if (!user || user.role !== "admin") return;
        setLoading(true); setErr(null);
        try {
            const [rk, rr, rp, rc] = await Promise.all([
                adminFetch("/api/web/admin/conversions/kpi", {}, accessToken).then(r => r.json()),
                adminFetch("/api/web/admin/conversions?" + qs, {}, accessToken).then(r => r.json()),
                adminFetch("/api/web/admin/conversions/by-publisher", {}, accessToken).then(r => r.json()),
                adminFetch("/api/web/admin/conversions/by-campaign", {}, accessToken).then(r => r.json()),
            ]);
            setKpi(rk); setRows(rr.rows || []); setTotal(rr.total || 0);
            setPublishers(rp.rows || []); setCampaigns(rc.rows || []);
        } catch (e) { setErr(e.message); if (e.status === 401) navigate("/admin/login", { replace: true }); }
        finally { setLoading(false); }
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [qs, user]);
    const exportCsv = async () => {
        try {
            const res = await adminFetch("/api/web/admin/conversions.csv?" + qs, {}, accessToken);
            const blob = await res.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url; a.download = `femcare-conversions-${new Date().toISOString().slice(0,10)}.csv`;
            document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
        } catch (e) { setErr(e.message); }
    };

    if (!user) return <div className="p-12 text-femcare-ink/60">Loading…</div>;

    return (
        <div className="min-h-screen bg-femcare-cream/40" data-testid="admin-conversions-page">
            <header className="bg-white border-b border-femcare-border">
                <div className="max-w-7xl mx-auto flex items-center justify-between px-6 py-4">
                    <div className="flex items-center gap-6">
                        <Link to="/admin" className="font-serif text-xl text-femcare-plum">Femcare Admin</Link>
                        <nav className="flex gap-4 text-sm text-femcare-ink/70">
                            <Link to="/admin" data-testid="admin-nav-dashboard">Dashboard</Link>
                            <Link to="/admin/conversions" className="text-femcare-plum font-semibold" data-testid="admin-nav-conversions">Web Conversions</Link>
                            <Link to="/admin/publisher-links" data-testid="admin-nav-publisher-links">Publisher Links</Link>
                        </nav>
                    </div>
                    <span className="text-xs text-femcare-ink/60">{user?.email}</span>
                </div>
            </header>

            <main className="max-w-7xl mx-auto px-6 py-8 space-y-6">
                {err && <div className="bg-red-50 text-red-700 rounded-xl p-4" data-testid="admin-error">{err}</div>}

                {/* KPI grid */}
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4" data-testid="kpi-grid">
                    {[
                        ["Confirmed", kpi?.confirmed_conversions ?? "–", "kpi-confirmed"],
                        ["Pending",   kpi?.pending_payments ?? "–",     "kpi-pending"],
                        ["Failed",    kpi?.failed_payments ?? "–",      "kpi-failed"],
                        ["India",     kpi?.india_conversions ?? "–",    "kpi-india"],
                        ["International", kpi?.international_conversions ?? "–", "kpi-intl"],
                        ["Affiliate", kpi?.affiliate_conversions ?? "–", "kpi-affiliate"],
                        ["Direct/Organic", kpi?.direct_conversions ?? "–", "kpi-direct"],
                        ["Revenue INR", fmt(kpi?.confirmed_revenue_inr_minor, "INR"), "kpi-rev-inr"],
                        ["Revenue USD", fmt(kpi?.confirmed_revenue_usd_minor, "USD"), "kpi-rev-usd"],
                    ].map(([label,val,tid]) => (
                        <div key={label} className="bg-white border border-femcare-border rounded-2xl p-4" data-testid={tid}>
                            <p className="text-[11px] uppercase text-femcare-ink/60 font-bold tracking-widest">{label}</p>
                            <p className="text-2xl font-extrabold text-femcare-plum mt-1">{val}</p>
                        </div>
                    ))}
                </div>

                {/* Tabs */}
                <div className="flex gap-2 border-b border-femcare-border">
                    {["table","publishers","campaigns"].map(t => (
                        <button key={t} onClick={()=>setTab(t)}
                            className={`px-4 py-2 text-sm font-semibold border-b-2 ${tab===t ? "border-femcare-plum text-femcare-plum" : "border-transparent text-femcare-ink/60"}`}
                            data-testid={`tab-${t}`}>{t==="table"?"All Conversions":(t==="publishers"?"By Publisher":"By Campaign")}</button>
                    ))}
                    <div className="ml-auto flex gap-2 py-2">
                        <button onClick={exportCsv} className="text-xs bg-femcare-plum text-white px-3 py-1.5 rounded-lg font-bold" data-testid="admin-export-csv">Export CSV</button>
                    </div>
                </div>

                {/* Filters */}
                {tab === "table" && (
                    <div className="flex flex-wrap gap-2 items-end">
                        <select value={filters.status} onChange={e=>setFilters(f=>({...f, status:e.target.value, page:1}))}
                            className="border border-femcare-border rounded-lg px-3 py-1.5 text-sm" data-testid="filter-status">
                            <option>CONFIRMED</option><option>PENDING</option><option>FAILED</option><option value="">All</option>
                        </select>
                        <input placeholder="Search order / payment / user / publisher…" value={filters.q}
                            onChange={e=>setFilters(f=>({...f, q:e.target.value, page:1}))}
                            className="border border-femcare-border rounded-lg px-3 py-1.5 text-sm min-w-[260px]" data-testid="filter-q" />
                        <input placeholder="Publisher ID" onBlur={e=>setFilters(f=>({...f, publisher_id:e.target.value, page:1}))}
                            className="border border-femcare-border rounded-lg px-3 py-1.5 text-sm" data-testid="filter-publisher" />
                        <select value={filters.per_page} onChange={e=>setFilters(f=>({...f, per_page:Number(e.target.value), page:1}))}
                            className="border border-femcare-border rounded-lg px-3 py-1.5 text-sm" data-testid="filter-per-page">
                            <option value={25}>25</option><option value={50}>50</option><option value={100}>100</option>
                        </select>
                    </div>
                )}

                {/* Content */}
                {loading && <p className="text-sm text-femcare-ink/60">Loading…</p>}
                {tab === "table" && (
                    <div className="bg-white border border-femcare-border rounded-2xl overflow-x-auto">
                        <table className="w-full text-sm" data-testid="conversions-table">
                            <thead className="bg-femcare-cream/50 text-left text-[11px] uppercase text-femcare-ink/60">
                                <tr>{["Date","Status","Country","User","Plan","Amount","Order","Payment","Publisher","Campaign","Landing"].map(h=><th key={h} className="px-3 py-2">{h}</th>)}</tr>
                            </thead>
                            <tbody>
                                {rows.map(r => (
                                    <tr key={r.rzp_order_id} className="border-t border-femcare-border/50">
                                        <td className="px-3 py-2 text-xs">{r.granted_at?.slice(0,19)}</td>
                                        <td className="px-3 py-2"><span className={`text-xs font-bold ${r.status==="CONFIRMED"?"text-green-700":(r.status==="PENDING"?"text-amber-700":"text-red-700")}`}>{r.status==="CONFIRMED"?"Confirmed — Razorpay Captured":r.status}</span></td>
                                        <td className="px-3 py-2 text-xs">{r.country}</td>
                                        <td className="px-3 py-2 text-xs font-mono">{r.ft_user_id?.slice(0,10)}…</td>
                                        <td className="px-3 py-2 text-xs">{r.plan_id}</td>
                                        <td className="px-3 py-2 text-xs">{fmt(r.amount_minor, r.currency)}</td>
                                        <td className="px-3 py-2 text-xs font-mono">{r.rzp_order_id?.slice(0,14)}…</td>
                                        <td className="px-3 py-2 text-xs font-mono">{r.rzp_payment_id?.slice(0,14) || "—"}</td>
                                        <td className="px-3 py-2 text-xs">{r.affiliate_ref || <span className="text-femcare-ink/40">direct</span>}</td>
                                        <td className="px-3 py-2 text-xs">{r.utm_source && `${r.utm_source} · ${r.utm_campaign||""}`}</td>
                                        <td className="px-3 py-2 text-xs text-femcare-ink/60 truncate max-w-[180px]">{r.landing_page}</td>
                                    </tr>
                                ))}
                                {rows.length === 0 && !loading && <tr><td colSpan={11} className="px-3 py-6 text-center text-femcare-ink/50" data-testid="empty">No conversions match these filters.</td></tr>}
                            </tbody>
                        </table>
                        {total > filters.per_page && (
                            <div className="flex items-center justify-between p-3 text-xs">
                                <span>Page {filters.page} of {Math.ceil(total/filters.per_page)}</span>
                                <div className="flex gap-2">
                                    <button onClick={()=>setFilters(f=>({...f, page:Math.max(1,f.page-1)}))} className="px-3 py-1 border rounded" data-testid="page-prev">Prev</button>
                                    <button onClick={()=>setFilters(f=>({...f, page:f.page+1}))} className="px-3 py-1 border rounded" data-testid="page-next">Next</button>
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {tab === "publishers" && (
                    <div className="bg-white border border-femcare-border rounded-2xl overflow-x-auto" data-testid="publishers-summary">
                        <table className="w-full text-sm">
                            <thead className="bg-femcare-cream/50 text-left text-[11px] uppercase text-femcare-ink/60">
                                <tr>{["Publisher","Confirmed","Revenue INR","Revenue USD"].map(h=><th key={h} className="px-3 py-2">{h}</th>)}</tr>
                            </thead>
                            <tbody>
                                {publishers.map(p => (
                                    <tr key={p._id} className="border-t border-femcare-border/50 cursor-pointer hover:bg-femcare-cream/40"
                                        onClick={()=>{setTab("table"); setFilters(f=>({...f, publisher_id: p._id === "(direct)" ? "" : p._id, page:1}));}}>
                                        <td className="px-3 py-2 font-mono text-xs">{p._id}</td>
                                        <td className="px-3 py-2 text-xs font-bold">{p.confirmed}</td>
                                        <td className="px-3 py-2 text-xs">{fmt(p.inr_minor, "INR")}</td>
                                        <td className="px-3 py-2 text-xs">{fmt(p.usd_minor, "USD")}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}

                {tab === "campaigns" && (
                    <div className="bg-white border border-femcare-border rounded-2xl overflow-x-auto" data-testid="campaigns-summary">
                        <table className="w-full text-sm">
                            <thead className="bg-femcare-cream/50 text-left text-[11px] uppercase text-femcare-ink/60">
                                <tr>{["Source","Medium","Campaign","Confirmed","Revenue INR","Revenue USD"].map(h=><th key={h} className="px-3 py-2">{h}</th>)}</tr>
                            </thead>
                            <tbody>
                                {campaigns.map((c,i) => (
                                    <tr key={i} className="border-t border-femcare-border/50">
                                        <td className="px-3 py-2 text-xs">{c._id.source}</td>
                                        <td className="px-3 py-2 text-xs">{c._id.medium}</td>
                                        <td className="px-3 py-2 text-xs">{c._id.campaign}</td>
                                        <td className="px-3 py-2 text-xs font-bold">{c.confirmed}</td>
                                        <td className="px-3 py-2 text-xs">{fmt(c.inr_minor, "INR")}</td>
                                        <td className="px-3 py-2 text-xs">{fmt(c.usd_minor, "USD")}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </main>
        </div>
    );
}
